import React, { createContext, useContext, ReactNode, useState, useEffect, useMemo, useRef } from 'react';
import api from '../../services/api';
import { ThemeConfig, defaultThemeTupi } from '../../constants/themes';
import { auth, Unsubscribe } from 'firebase';

interface ThemeProviderProps {
  children: ReactNode;
}

interface ThemeContextType {
  theme: ThemeConfig;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const authStateChangedUnsubscribe = useRef<Unsubscribe>();
  const [theme, setTheme] = useState<ThemeConfig>(defaultThemeTupi);

  const fetchTheme = async () => {
    try {
      const { data } = await api.get('/themes');
      setTheme({
        ...defaultThemeTupi,
        ...data.theme,
        logos: {
          collapsed: data.theme?.logos?.collapsed ?? defaultThemeTupi.logos.collapsed,
          notCollapsed: data.theme?.logos?.notCollapsed ?? defaultThemeTupi.logos.notCollapsed,
        },
      });
    } catch (error) {
      console.error('Erro ao buscar tema do backend', error);
      setTheme(defaultThemeTupi);
    }
  };

  useEffect(() => {
    authStateChangedUnsubscribe.current = auth().onAuthStateChanged(async (user) => {
      fetchTheme();
    });
  
    return () => {
      if (authStateChangedUnsubscribe.current)
        authStateChangedUnsubscribe.current();
    };
  }, []);


  useEffect(() => {
    const faviconHref = theme.browserTabFavicon;
    if (faviconHref) {
      const favicon = document.getElementById('favicon') as HTMLLinkElement | null;
      if (favicon) favicon.href = faviconHref;
    }
  }, [theme.browserTabFavicon]);

  useEffect(() => {
    const title = theme.browserTabTitle;
    if (title) {
      document.title = title;
    }
  }, [theme.browserTabTitle]);

  const setThemeToDefault = () => {
    setTheme(defaultThemeTupi);
  };

  const themeContextValue = useMemo(() => {
    const customStyle = {
      '--whitelabel-bg': theme.menuBackgroundColor,
      '--whitelabel-text': theme.menuTextColor,
      '--whitelabel-selected-bg': theme.menuSelectedBackgroundColor,
      '--whitelabel-selected-text': theme.menuSelectedTextColor,
      '--whitelabel-link': theme.linkColor,
      '--whitelabel-submenu-bg': theme.submenuBackgroundColor,
      '--whitelabel-submenu-text': theme.submenuTextColor,
      '--whitelabel-hover-color': theme.menuHoverColor,
      '--whitelabel-submenu-hover-color': theme.submenuHoverColor,
      '--whitelabel-submenu-shadow': theme.submenuShadow,
    } as React.CSSProperties;
    return { theme, customStyle, setThemeToDefault };
  }, [theme]);

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <div style={themeContextValue.customStyle}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 